export type HelpTopic =
  | 'All Topics'
  | 'Dashboard'
  | 'Accounts'
  | 'Transactions'
  | 'Budgets'
  | 'Goals'
  | 'Insights'
  | 'Reports'
  | 'Settings';

export interface HelpArticle {
  slug: string;
  title: string;
  excerpt: string;
  topic: HelpTopic;
  tags: string[];
  isPopular?: boolean;
  markdown: string;
}

const TOPIC_BY_SLUG: Record<string, HelpTopic> = {
  dashboard: 'Dashboard',
  accounts: 'Accounts',
  transactions: 'Transactions',
  budgets: 'Budgets',
  goals: 'Goals',
  insights: 'Insights',
  reporting: 'Reports',
  settings: 'Settings',
  'settings-profile': 'Settings',
  'settings-account': 'Settings',
  'settings-categorisation': 'Settings',
  'settings-ai': 'Settings',
  'settings-security': 'Settings',
  'settings-notifications': 'Settings',
  'settings-rules': 'Settings',
};

const TAGS_BY_SLUG: Record<string, string[]> = {
  dashboard: ['overview', 'widgets', 'customisation'],
  transactions: ['transactions', 'import', 'search', 'filters'],
  accounts: ['accounts', 'net worth', 'export'],
  budgets: ['budgets', 'spending', 'limits', 'alerts'],
  goals: ['goals', 'savings', 'targets', 'progress'],
  insights: ['insights', 'analysis', 'trends', 'ai', 'recommendations'],
  reporting: ['reports', 'export', 'tax', 'financial planning'],
  settings: ['settings', 'preferences', 'security', 'notifications', 'customization'],
};

const POPULAR_SLUGS = new Set(['dashboard', 'transactions', 'accounts']);

function slugFromPath(path: string): string {
  const file = path.split('/').pop() ?? path;
  return file.replace(/\.md$/i, '').trim().toLowerCase();
}

function firstHeading(markdown: string): string | undefined {
  const match = markdown.match(/^#\s+(.+)\s*$/m);
  return match?.[1]?.trim();
}

function parseTagsFromMarkdown(markdown: string): string[] | undefined {
  // Look for lines matching "tag:" or "tags:" (case-insensitive)
  const lines = markdown.split('\n');
  for (const line of lines) {
    const trimmed = line.trim();
    // Match "tag:" or "tags:" at the start of the line (case-insensitive)
    const match = trimmed.match(/^(?:tag|tags):\s*(.+)$/i);
    if (match) {
      // Split by comma, trim each tag, filter out empty strings
      const tags = match[1]
        .split(',')
        .map((tag) => tag.trim())
        .filter((tag) => tag.length > 0);
      return tags.length > 0 ? tags : undefined;
    }
  }
  return undefined;
}

function firstParagraph(markdown: string): string {
  // Find the first non-empty paragraph that isn't a heading or horizontal rule.
  const lines = markdown.split('\n');
  const buffer: string[] = [];

  const flush = (): string | undefined => {
    const text = buffer.join(' ').trim();
    return text.length > 0 ? text : undefined;
  };

  for (const rawLine of lines) {
    const line = rawLine.trim();
    if (!line) {
      const paragraph = flush();
      if (paragraph) return paragraph;
      buffer.length = 0;
      continue;
    }
    if (line.startsWith('#')) continue;
    if (line === '---') continue;
    // Skip tag lines when extracting first paragraph
    if (/^(?:tag|tags):/i.test(line)) continue;
    buffer.push(line);
  }

  return flush() ?? '';
}

// Load markdown files from the repo root `support/` directory.
// Vite will include these at build time.
const SUPPORT_MARKDOWN_MODULES = import.meta.glob('../../support/*.md', {
  eager: true,
  query: '?raw',
  import: 'default',
} as any) as unknown as Record<string, string>;

export const HELP_ARTICLES: HelpArticle[] = Object.entries(SUPPORT_MARKDOWN_MODULES)
  .map(([path, markdown]) => {
    const slug = slugFromPath(path);
    const title = firstHeading(markdown) ?? slug;
    const excerpt = firstParagraph(markdown);
    const topic = TOPIC_BY_SLUG[slug] ?? 'All Topics';
    // Try to parse tags from markdown first, fall back to hardcoded tags
    const parsedTags = parseTagsFromMarkdown(markdown);
    const tags = parsedTags ?? TAGS_BY_SLUG[slug] ?? [];

    return {
      slug,
      title,
      excerpt,
      topic,
      tags,
      isPopular: POPULAR_SLUGS.has(slug),
      markdown,
    };
  })
  // Prefer stable ordering in the UI
  .sort((a, b) => a.title.localeCompare(b.title));

export function getHelpArticleBySlug(slug: string): HelpArticle | undefined {
  const normalized = slug.trim().toLowerCase();
  return HELP_ARTICLES.find((a) => a.slug === normalized);
}

export function getHelpTopicsWithCounts(
  articles: HelpArticle[]
): Array<{ topic: HelpTopic; count: number }> {
  const counts = new Map<HelpTopic, number>();
  for (const article of articles) {
    counts.set(article.topic, (counts.get(article.topic) ?? 0) + 1);
  }

  const topics: HelpTopic[] = [
    'All Topics',
    'Dashboard',
    'Transactions',
    'Accounts',
    'Budgets',
    'Goals',
    'Insights',
    'Reports',
    'Settings',
  ];

  const total = articles.length;
  return topics
    .map((topic) => ({
      topic,
      count: topic === 'All Topics' ? total : (counts.get(topic) ?? 0),
    }))
    .filter((t) => t.topic === 'All Topics' || t.count > 0);
}

export function filterHelpArticles(args: {
  articles: HelpArticle[];
  topic: HelpTopic;
  query: string;
}): HelpArticle[] {
  const normalizedQuery = args.query.trim().toLowerCase();
  const hasQuery = normalizedQuery.length > 0;

  return args.articles.filter((article) => {
    // When search query is active, ignore topic filter and search across all articles
    // When no query, respect the topic filter
    if (!hasQuery) {
      if (args.topic !== 'All Topics' && article.topic !== args.topic) {
        return false;
      }
      return true;
    }

    // Search full markdown content along with title, excerpt, topic, and tags
    const haystack = [
      article.title,
      article.excerpt,
      article.topic,
      article.tags.join(' '),
      article.markdown, // Search full article content
    ]
      .join(' ')
      .toLowerCase();

    return haystack.includes(normalizedQuery);
  });
}


